import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  AlertTriangle, Check, CheckCircle2, Clock, Copy, FileDown, FileText, Link2, Loader2, Lock, PenLine, RefreshCw, Undo2, Upload, X,
} from 'lucide-react';
import type { GbuHinweis, GbuUnterschrift, GbuVoll, Kataster, UnterschriftRolle } from '../../../services/sifa/gbuTypes';
import { baueFassungsInhalt, naechstePruefung } from '../../../services/sifa/gbuLogic';
import {
  type GbuBenutzer, GbuRpcFehler, fassungEinfrieren, freigabeZurueckziehen, freigeben, legeFreigabeLinkAn, legeScanAb, unterschreibeFassung,
} from '../../../services/sifa/gbuService';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { PAPIER_DATEI_TYPEN, pruefePapierDatei } from '../../sige-einweisung/einweisungLogic';
import { SignaturePad } from '../../common/SignaturePad';
import { RueckfrageDialog } from '../vorlagen/RueckfrageDialog';
import { gbuPdfAusgeben } from './gbuAusgabe';
import {
  HINWEIS_TON,
  ROLLE_LABEL,
  fehlendeDigitaleUnterschrift,
  formatTag,
  istIsoDatum,
  standGeaendert,
  freigabeLinkUrl,
  geraeteUnterschrift,
  gueltigeFassung,
  offeneFassung,
  pdfOptionenFuer,
  pruefePruefdatum,
  rolleVorgabe,
  rollenDerGbu,
  sofortAnhaengen,
  vereinigeUnterschriften,
} from './gbuModulLogic';

export interface FreigabeMeta {
  kundeName: string;
  kundeAnschrift: string | null;
  bueroName: string;
}

interface Props {
  voll: GbuVoll;
  kataster: Kataster;
  /** Feste Hinweise — Warnungen werden hervorgehoben, sperren aber nicht (O4). */
  hinweise: GbuHinweis[];
  meta: FreigabeMeta;
  projectId: string;
  benutzer: GbuBenutzer;
  heute: string;
  isReadOnly: boolean;
  onClose: () => void;
  /** Frischer Stand nach jedem Schritt (einfrieren, freigeben, Link geprueft). */
  onNeuLaden: () => Promise<GbuVoll | null>;
  /** Die Fassung ist freigegeben — der Editor legt das PDF ab. */
  onFreigegeben: (voll: GbuVoll) => void;
  onZurueckgezogen: () => void;
  /** Zeit buchen anbieten (Plan §10.1: „dazu ein Angebot im Freigabedialog"). */
  onZeitBuchen?: () => void;
}

type Weg = 'digital' | 'papier';

const FELD = 'w-full px-3 py-2 min-h-[44px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * Freigabe einer GBU (Plan §8) in zwei Schritten.
 *
 * 1. EINFRIEREN (Status `entwurf`): Pruefdatum (Vorgabe heute + 12 Monate,
 *    aenderbar), Warnungen sichtbar, dann baueFassungsInhalt +
 *    fassungEinfrieren — die GBU steht danach `in_freigabe`.
 * 2. WEG WAEHLEN (Status `in_freigabe`):
 *    - digital: „Erstellt" und „Geprüft" am Geraet (SignaturePad) werden
 *      SOFORT an die Fassung gehaengt (unterschreibeFassung, danach neu
 *      laden — die Revision aendert sich). „Freigegeben" dann entweder am
 *      Geraet (freigeben mit genau dieser Unterschrift) oder per Freigabe-Link
 *      fuer den Unternehmer (QR + kopieren); die vorhandenen Unterschriften
 *      bleiben dabei erhalten.
 *    - Papier: PDF mit leeren Feldern herunterladen, Scan hochladen
 *      (legeScanAb), dann freigeben mit `scanDokumentId`.
 *    „Zurückziehen" geht, solange nicht freigegeben.
 *
 * Nach der Freigabe legt der Editor das PDF ab (onFreigegeben).
 */
export const GbuFreigabeDialog: React.FC<Props> = ({
  voll, kataster, hinweise, meta, projectId, benutzer, heute, isReadOnly, onClose, onNeuLaden, onFreigegeben, onZurueckgezogen, onZeitBuchen,
}) => {
  const gbu = voll.gbu;
  const offen = offeneFassung(voll);
  const gueltig = gbu.status === 'freigegeben' ? gueltigeFassung(voll) : null;

  const [pruefenBis, setPruefenBis] = useState(() => naechstePruefung(heute, 12));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [weg, setWeg] = useState<Weg>('digital');
  const [zurueckFrage, setZurueckFrage] = useState(false);
  const laeuftRef = useRef(false);

  // Digital
  /** „Freigegeben" am Geraet — erst „Freigeben" schickt sie (sie IST die Freigabe). */
  const [freigabeUnterschrift, setFreigabeUnterschrift] = useState<GbuUnterschrift | null>(null);
  const [namen, setNamen] = useState<Record<UnterschriftRolle, string>>(() => ({
    erstellt: rolleVorgabe(gbu.rollen, 'erstellt').name,
    geprueft: rolleVorgabe(gbu.rollen, 'geprueft').name,
    freigegeben: rolleVorgabe(gbu.rollen, 'freigegeben').name,
  }));
  const [signiere, setSigniere] = useState<UnterschriftRolle | null>(null);
  const [link, setLink] = useState<{ url: string; gueltigBis: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);

  // Papier
  const dateiRef = useRef<HTMLInputElement>(null);
  const [datei, setDatei] = useState<File | null>(null);
  const [papierDatum, setPapierDatum] = useState(heute);
  /** Wer laut Scan unterschrieben hat — die Namen gehen ohne Bild mit (Weg „papier"). */
  const [papierRollen, setPapierRollen] = useState<ReadonlySet<UnterschriftRolle>>(() => new Set(rollenDerGbu(gbu.rollen)));
  /** Der Scan des ersten Anlaufs — ein zweiter Klick nach einem Fehler laedt nicht noch einmal hoch. */
  const [scan, setScan] = useState<{ fassungId: string; dokumentId: string } | null>(null);

  const warnungen = useMemo(() => hinweise.filter((h) => h.stufe !== 'sperre'), [hinweise]);
  const sperren = useMemo(() => hinweise.filter((h) => h.stufe === 'sperre'), [hinweise]);
  const rollen = rollenDerGbu(offen?.inhalt?.rollen ?? gbu.rollen);
  const anDerFassung = offen?.unterschriften ?? [];
  const alle = vereinigeUnterschriften(anDerFassung, freigabeUnterschrift ? [freigabeUnterschrift] : []);
  const fehltDigital = fehlendeDigitaleUnterschrift(alle);

  useEffect(() => {
    if (!link) return;
    let aktiv = true;
    QRCode.toDataURL(link.url, { width: 360, margin: 2 })
      .then((d) => { if (aktiv) setQr(d); })
      .catch(() => { if (aktiv) setQr(null); });
    return () => { aktiv = false; };
  }, [link]);

  /** Ein Schritt mit Riegel gegen den zweiten Fingertipp und sichtbarem Fehler. */
  const schritt = async (f: () => Promise<void>) => {
    if (laeuftRef.current || isReadOnly) return;
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      await f();
    } catch (e) {
      // Der Stand passt nicht mehr (anderswo freigegeben, zurueckgezogen …): gleich frisch laden statt nur „bitte neu laden".
      if (e instanceof GbuRpcFehler && (e.fehler === 'status' || e.fehler === 'unbekannt')) {
        setFehler(`${rechtFehlerOder(e)} Der Stand wurde neu geladen.`);
        try { await onNeuLaden(); } catch { /* der Editor zeigt den Ladefehler */ }
      } else {
        setFehler(rechtFehlerOder(e));
      }
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  };

  /** „Neu laden" an der Fehlerzeile. */
  const neuLadenVonHand = () => schritt(async () => { await onNeuLaden(); });

  const einfrieren = () => schritt(async () => {
    const grund = pruefePruefdatum(pruefenBis, heute);
    if (grund) throw new Error(grund);
    // Eingefroren wird der FRISCHE Stand — nie der zuletzt angezeigte, wenn inzwischen jemand weitergearbeitet hat.
    const frisch = await onNeuLaden();
    if (!frisch) throw new Error('Der aktuelle Stand ließ sich nicht laden — es wurde nichts eingefroren.');
    if (frisch.gbu.status !== 'entwurf') throw new Error('Die Beurteilung ist inzwischen nicht mehr im Entwurf — es wurde nichts eingefroren.');
    if (standGeaendert(voll, frisch)) {
      throw new Error('Der Stand hat sich inzwischen geändert (anderes Fenster oder anderer Bearbeiter) und wurde neu geladen. Bitte prüfen und erneut einfrieren.');
    }
    const inhalt = baueFassungsInhalt(frisch, kataster, {
      kundeName: meta.kundeName,
      kundeAnschrift: meta.kundeAnschrift,
      bueroName: meta.bueroName,
      erstelltAm: new Date().toISOString(),
      pruefenBis,
    });
    await fassungEinfrieren(frisch.gbu.id, inhalt, pruefenBis);
    await onNeuLaden();
  });

  const nachFreigabe = async () => {
    const frisch = await onNeuLaden();
    if (frisch && frisch.gbu.status === 'freigegeben') onFreigegeben(frisch);
  };

  const digitalFreigeben = () => schritt(async () => {
    if (!offen) throw new Error('Keine Fassung in Freigabe — bitte neu laden.');
    if (fehltDigital) throw new Error(fehltDigital);
    // Nur die Unterschrift „Freigegeben" geht mit — „Erstellt"/„Geprüft" haengen schon an der Fassung.
    await freigeben(offen.id, { weg: 'digital', unterschriften: freigabeUnterschrift ? [freigabeUnterschrift] : [] });
    setFreigabeUnterschrift(null);
    await nachFreigabe();
  });

  const linkAnlegen = () => schritt(async () => {
    if (!offen) throw new Error('Keine Fassung in Freigabe — bitte neu laden.');
    const name = (namen.freigegeben || '').trim();
    const erg = await legeFreigabeLinkAn(offen.id, { rolle: 'freigegeben', name: name || null, tage: 14 });
    setLink({ url: freigabeLinkUrl(window.location.origin, window.location.pathname, erg.token), gueltigBis: erg.gueltigBis });
  });

  const standPruefen = () => schritt(async () => {
    const frisch = await onNeuLaden();
    if (frisch && frisch.gbu.status === 'freigegeben') onFreigegeben(frisch);
  });

  const papierFreigeben = () => schritt(async () => {
    if (!offen) throw new Error('Keine Fassung in Freigabe — bitte neu laden.');
    const pruefung = pruefePapierDatei(datei);
    const schonDa = scan?.fassungId === offen.id ? scan.dokumentId : null;
    if (!istIsoDatum(papierDatum) || papierDatum > heute) throw new Error('Bitte das Datum der Unterschrift angeben — ein gültiges Datum, heute oder früher.');
    if (!papierRollen.has('freigegeben')) throw new Error('Ohne die Unterschrift „Freigegeben" (Unternehmer) ist die Fassung nicht freigegeben.');
    if (!schonDa && !pruefung.ok) throw new Error(pruefung.fehler || 'Bitte den unterschriebenen Scan wählen.');
    const dokumentId = schonDa ?? await legeScanAb(projectId, gbu, offen, datei as File, benutzer);
    setScan({ fassungId: offen.id, dokumentId });
    // Die Namen der Rollen gehen mit (ohne Bild, Weg „papier") — das PDF nennt dann, wer auf Papier unterschrieben hat.
    const datum = `${papierDatum}T12:00:00.000Z`;
    // Rollen, die schon am Geraet unterschrieben haben, bleiben, wie sie sind.
    const papierUnterschriften: GbuUnterschrift[] = rollen
      .filter((r) => !anDerFassung.some((u) => u.rolle === r))
      .map((r) => ({ rolle: r, ...rolleVorgabe(offen.inhalt?.rollen ?? gbu.rollen, r) }))
      .filter((r) => r.name && papierRollen.has(r.rolle))
      .map((r) => ({ rolle: r.rolle, name: r.name, funktion: r.funktion || null, datum, bild: null, weg: 'papier' as const }));
    await freigeben(offen.id, { weg: 'papier', unterschriften: papierUnterschriften, scanDokumentId: dokumentId });
    await nachFreigabe();
  });

  const pdfLeer = () => schritt(async () => {
    if (!offen) throw new Error('Keine Fassung in Freigabe — bitte neu laden.');
    await gbuPdfAusgeben(offen.inhalt, pdfOptionenFuer('freigabe_papier', offen));
  });

  const zurueckziehen = () => schritt(async () => {
    if (!offen) return;
    setZurueckFrage(false);
    await freigabeZurueckziehen(offen.id);
    await onNeuLaden();
    onZurueckgezogen();
  });

  const kopieren = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      // Zwischenablage gesperrt: der Link steht zum Markieren darunter.
      setFehler('Kopieren nicht möglich — den Link unten markieren und kopieren.');
    }
  };

  /**
   * Nach dem Zeichnen: „Erstellt"/„Geprüft" sofort an die Fassung (und neu
   * laden — die Revision aendert sich), „Freigegeben" nur vormerken.
   */
  const unterschreiben = (rolle: UnterschriftRolle, bild: string) => {
    setSigniere(null);
    const u = geraeteUnterschrift({
      rolle,
      name: namen[rolle],
      vorgabe: rolleVorgabe(offen?.inhalt?.rollen ?? gbu.rollen, rolle),
      bild,
      jetzt: new Date().toISOString(),
    });
    if (!sofortAnhaengen(rolle)) { setFreigabeUnterschrift(u); return; }
    void schritt(async () => {
      if (!offen) throw new Error('Keine Fassung in Freigabe — bitte neu laden.');
      await unterschreibeFassung(offen.id, [u]);
      await onNeuLaden();
    });
  };

  if (signiere) {
    return (
      <SignaturePad
        alsOverlay
        titel={`Unterschrift „${ROLLE_LABEL[signiere]}" — ${(namen[signiere] || '').trim() || 'ohne Namen'}`}
        hinweis={`GBU Nr. ${gbu.nummer}, Fassung ${offen?.fassung ?? gbu.fassung}`}
        onUebernehmen={(d) => unterschreiben(signiere, d)}
        onAbbrechen={() => setSigniere(null)}
      />
    );
  }

  const warnBlock = warnungen.length > 0 && (
    <div className="space-y-1">
      <p className="text-[11px] font-black uppercase tracking-wide text-amber-700">Bitte beachten (sperrt die Freigabe nicht)</p>
      {warnungen.map((h, i) => <p key={`${h.code}-${i}`} className={`px-3 py-1.5 text-xs border rounded-lg ${HINWEIS_TON[h.stufe]}`}>{h.text}</p>)}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">
              {gbu.status === 'entwurf' ? 'Zur Freigabe' : gbu.status === 'in_freigabe' ? `Freigabe Fassung ${offen?.fassung ?? gbu.fassung}` : 'Freigegeben'}
            </h3>
            <p className="text-xs text-slate-500 truncate">Nr. {gbu.nummer} — {gbu.titel}</p>
          </div>
          <button onClick={onClose} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* ── Schritt 1: Einfrieren ─────────────────────────────────── */}
          {gbu.status === 'entwurf' && (
            <>
              <p className="text-sm text-slate-700">
                Der aktuelle Stand wird als <b>Fassung {gbu.fassung}</b> eingefroren. Danach ist die Beurteilung schreibgeschützt,
                bis sie freigegeben oder zurückgezogen wird{gbu.freigegebeneFassung != null ? `; Fassung ${gbu.freigegebeneFassung} bleibt bis dahin gültig` : ''}.
              </p>
              {sperren.length > 0 && (
                <div className="space-y-1">
                  {sperren.map((h, i) => <p key={`${h.code}-${i}`} className={`px-3 py-1.5 text-xs border rounded-lg ${HINWEIS_TON.sperre}`}>{h.text}</p>)}
                </div>
              )}
              {warnBlock}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={LABEL}>Nächste Überprüfung bis</span>
                  <input type="date" value={pruefenBis} min={heute} onChange={(e) => setPruefenBis(e.target.value)} disabled={laeuft} className={FELD} />
                  <span className="block text-[11px] text-slate-500 mt-1">Vorgabe: 12 Monate. Bei der Freigabe entsteht dafür eine Frist.</span>
                </label>
                <div className="text-xs text-slate-600 space-y-1">
                  <p className={LABEL}>Rollen</p>
                  {rollenDerGbu(gbu.rollen).map((r) => (
                    <p key={r}><b>{ROLLE_LABEL[r]}:</b> {rolleVorgabe(gbu.rollen, r).name || <span className="text-red-600">fehlt</span>}</p>
                  ))}
                  <p className="text-[11px] text-slate-500">Ändern im Editor unter „Rollen".</p>
                </div>
              </div>
            </>
          )}

          {/* ── Schritt 2: Weg waehlen ────────────────────────────────── */}
          {gbu.status === 'in_freigabe' && offen && (
            <>
              <p className="text-xs text-slate-600 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Fassung {offen.fassung} ist eingefroren · Prüfung bis {formatTag(gbu.pruefenBis)}
              </p>
              {warnBlock}
              <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl">
                {(['digital', 'papier'] as Weg[]).map((w) => (
                  <button key={w} onClick={() => setWeg(w)} className={`min-h-[40px] text-sm font-bold rounded-lg ${weg === w ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                    {w === 'digital' ? 'Digital' : 'Papier'}
                  </button>
                ))}
              </div>

              {weg === 'digital' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Unterschriften am Gerät</p>
                    <p className="text-[11px] text-slate-500">„Erstellt" und „Geprüft" hängen sofort an der Fassung und bleiben auch bei einer Freigabe per Link erhalten.</p>
                    {rollen.map((r) => {
                      const u = alle.find((x) => x.rolle === r);
                      const angehaengt = anDerFassung.some((x) => x.rolle === r);
                      return (
                        <div key={r} className="border border-slate-200 rounded-xl p-3 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-bold text-slate-800">{ROLLE_LABEL[r]}{r === 'geprueft' ? ' (optional)' : r === 'freigegeben' ? ' (Unternehmer)' : ''}</p>
                            {u && (
                              <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${angehaengt ? 'text-emerald-700' : 'text-blue-700'}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {u.weg === 'link' ? 'per Link unterschrieben' : angehaengt ? `unterschrieben ${formatTag(u.datum)}` : 'unterschrieben — gilt mit „Freigeben"'}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              value={namen[r]}
                              onChange={(e) => setNamen((n) => ({ ...n, [r]: e.target.value }))}
                              disabled={laeuft || u?.weg === 'link'}
                              placeholder="Name"
                              className={`${FELD} flex-1`}
                            />
                            {u?.bild && <img src={u.bild} alt={`Unterschrift ${ROLLE_LABEL[r]}`} className="h-11 max-w-[160px] object-contain bg-white border border-slate-200 rounded-lg" />}
                            {u?.weg !== 'link' && (
                              <button onClick={() => setSigniere(r)} disabled={laeuft || !(namen[r] || '').trim()} className="min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-40">
                                <PenLine className="w-3.5 h-3.5" /> {u ? 'Neu unterschreiben' : 'Unterschreiben'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Freigabe-Link für den Unternehmer</p>
                    <p className="text-xs text-slate-600">
                      Ohne Login: der Unternehmer sieht das PDF dieser Fassung, trägt seinen Namen ein und unterschreibt. Damit ist die
                      Fassung freigegeben; „Erstellt" und „Geprüft" bleiben erhalten — also vorher hier unterschreiben. Gültig 14 Tage, nur einmal.
                    </p>
                    {!link ? (
                      <button onClick={() => void linkAnlegen()} disabled={laeuft} className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg disabled:opacity-40">
                        {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link erzeugen
                      </button>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 items-start">
                        {qr ? <img src={qr} alt="QR-Code zum Freigabe-Link" className="w-full max-w-[180px] mx-auto rounded-lg border border-slate-200" /> : <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>}
                        <div className="space-y-2">
                          <p className="text-[11px] text-slate-500 break-all select-all">{link.url}</p>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> gültig bis {formatTag(link.gueltigBis)}</p>
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => void kopieren()} className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg">
                              {kopiert ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {kopiert ? 'Kopiert' : 'Link kopieren'}
                            </button>
                            <button onClick={() => void standPruefen()} disabled={laeuft} className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40">
                              <RefreshCw className="w-3.5 h-3.5" /> Stand prüfen
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {fehltDigital && <p className="text-xs text-slate-600">{fehltDigital}</p>}
                  {!alle.some((x) => x.rolle === 'erstellt') && (
                    <p className="text-xs text-amber-700">„Erstellt" ist noch nicht unterschrieben — die Freigabe geht trotzdem.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <ol className="text-xs text-slate-600 list-decimal pl-4 space-y-1">
                    <li>PDF mit leeren Unterschriftsfeldern herunterladen und drucken.</li>
                    <li>Unterschreiben lassen.</li>
                    <li>Scan oder Foto hochladen — damit ist die Fassung freigegeben.</li>
                  </ol>
                  <button onClick={() => void pdfLeer()} disabled={laeuft} className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40">
                    <FileDown className="w-4 h-4" /> PDF zum Unterschreiben
                  </button>
                  <button
                    type="button"
                    onClick={() => dateiRef.current?.click()}
                    disabled={laeuft}
                    className="w-full min-h-[80px] px-4 py-3 flex flex-col items-center justify-center gap-2 text-sm font-semibold text-slate-600 bg-slate-50 border border-dashed border-slate-300 hover:border-blue-400 rounded-xl disabled:opacity-40"
                  >
                    {datei ? <FileText className="w-6 h-6 text-blue-600" /> : <Upload className="w-6 h-6 text-slate-400" />}
                    <span className="truncate max-w-full">{datei ? datei.name : 'Unterschriebenen Scan wählen (PDF oder Bild)'}</span>
                  </button>
                  <input
                    ref={dateiRef}
                    type="file"
                    accept={PAPIER_DATEI_TYPEN}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setDatei(f);
                      setScan(null);
                      const p = pruefePapierDatei(f);
                      setFehler(f && !p.ok ? p.fehler || null : null);
                    }}
                  />
                  <label className="block max-w-xs">
                    <span className={LABEL}>Unterschrieben am</span>
                    <input type="date" value={papierDatum} max={heute} onChange={(e) => setPapierDatum(e.target.value)} disabled={laeuft} className={FELD} />
                  </label>
                  <div className="space-y-1">
                    <p className={LABEL}>Auf dem Papier unterschrieben haben</p>
                    {rollen.map((r) => {
                      const v = rolleVorgabe(offen.inhalt?.rollen ?? gbu.rollen, r);
                      const amGeraet = anDerFassung.some((x) => x.rolle === r);
                      return (
                        <label key={r} className="flex items-center gap-2 text-sm text-slate-700 min-h-[36px]">
                          <input
                            type="checkbox"
                            checked={amGeraet || papierRollen.has(r)}
                            disabled={laeuft || !v.name || amGeraet}
                            onChange={() => setPapierRollen((s) => { const n = new Set(s); if (n.has(r)) n.delete(r); else n.add(r); return n; })}
                          />
                          <span>
                            <b>{ROLLE_LABEL[r]}:</b> {v.name || <span className="text-red-600">kein Name in den Rollen</span>}
                            {amGeraet && <span className="text-slate-500"> — bereits am Gerät unterschrieben</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {gbu.status === 'in_freigabe' && !offen && (
            <p className="text-sm text-slate-600">Die eingefrorene Fassung wurde nicht gefunden — bitte neu laden.</p>
          )}

          {/* ── Fertig ────────────────────────────────────────────────── */}
          {gbu.status === 'freigegeben' && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-900 space-y-1">
              <p className="font-bold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Fassung {gbu.freigegebeneFassung} ist freigegeben{gueltig?.freigegebenAm ? ` (${formatTag(gueltig.freigegebenAm)})` : ''}.</p>
              <p className="text-xs">Die Überprüfungsfrist bis {formatTag(gbu.pruefenBis)} steht in „Fälligkeiten". Das PDF wird in „Dokumente" abgelegt.</p>
            </div>
          )}

          {fehler && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span className="flex-1">{fehler}</span>
              <button onClick={() => void neuLadenVonHand()} disabled={laeuft} className="shrink-0 inline-flex items-center gap-1 font-bold underline disabled:opacity-40">
                <RefreshCw className="w-3.5 h-3.5" /> Neu laden
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex flex-wrap justify-between gap-2 shrink-0">
          <div className="flex gap-2">
            {gbu.status === 'in_freigabe' && offen && (
              <button onClick={() => setZurueckFrage(true)} disabled={laeuft} className="min-h-[44px] px-3 flex items-center gap-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 rounded-xl disabled:opacity-40">
                <Undo2 className="w-4 h-4" /> Zurückziehen
              </button>
            )}
            {onZeitBuchen && gbu.status !== 'archiviert' && (
              <button onClick={onZeitBuchen} disabled={laeuft} className="min-h-[44px] px-3 flex items-center gap-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 rounded-xl disabled:opacity-40">
                <Clock className="w-4 h-4" /> Zeit buchen
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={laeuft} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">
              {gbu.status === 'freigegeben' ? 'Schließen' : 'Später'}
            </button>
            {gbu.status === 'entwurf' && (
              <button onClick={() => void einfrieren()} disabled={laeuft || sperren.length > 0 || isReadOnly} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40">
                {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Fassung {gbu.fassung} einfrieren
              </button>
            )}
            {gbu.status === 'in_freigabe' && offen && weg === 'digital' && (
              <button onClick={() => void digitalFreigeben()} disabled={laeuft || !!fehltDigital || isReadOnly} title={fehltDigital ?? undefined} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-40">
                {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Freigeben
              </button>
            )}
            {gbu.status === 'in_freigabe' && offen && weg === 'papier' && (
              <button onClick={() => void papierFreigeben()} disabled={laeuft || (!datei && !scan) || isReadOnly} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-40">
                {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Scan hochladen und freigeben
              </button>
            )}
          </div>
        </div>
      </div>

      {zurueckFrage && (
        <RueckfrageDialog
          titel="Freigabe zurückziehen"
          text={`Fassung ${offen?.fassung ?? ''} wird verworfen, offene Freigabe-Links werden ungültig. Die Beurteilung ist danach wieder ein bearbeitbarer Entwurf.`}
          jaText="Zurückziehen"
          gefaehrlich
          laeuft={laeuft}
          onNein={() => setZurueckFrage(false)}
          onJa={() => void zurueckziehen()}
        />
      )}
    </div>
  );
};
